import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { database } from "../../firebase";
import { ref, onValue, update, off, get, child } from "firebase/database";
import "./AddressEditor.css";

export default function AddressEditor({ userID, uniqueKey, onDone }) {
  const recordPath = `data/scans/${userID}/${uniqueKey}`;
  const addressRef = ref(database, `${recordPath}/googleMapAddress`);
  const parentRef = ref(database, recordPath);

  const [loading, setLoading] = useState(true);
  const [originalAddress, setOriginalAddress] = useState("");
  const [editedAddress, setEditedAddress] = useState("");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const unsubscribe = onValue(addressRef, (snap) => {
      const val = snap.val();

      if (val) {
        setOriginalAddress(val);
        // Only set the edited address if the user hasn't typed anything yet
        setEditedAddress((prev) => (prev && prev.length ? prev : val));
        setLoading(false);
        return;
      }

      setLoading(true);
      setOriginalAddress("");
    });

    return () => {
      off(addressRef);
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [addressRef]);

  const writeAddedToMauticFalse = async (fields = {}) => {
    // Critical: Only writing this triggers the Mautic update
    const payload = { ...fields, addedToMautic: false };
    return update(parentRef, payload);
  };

  const handleSave = async () => {
    setStatus("saving");

    try {
      // 1. Save the Address / Mautic flag
      if (!editedAddress || editedAddress === originalAddress) {
        await writeAddedToMauticFalse({});
      } else {
        await writeAddedToMauticFalse({ editedAddress });
      }

      setStatus("saved");

      // 2. Check DB for image existence before finishing
      // We look for 'urlToReadImage' in the current record
      const imageSnapshot = await get(child(parentRef, "urlToReadImage"));
      const hasImage = imageSnapshot.exists() && !!imageSnapshot.val();

      // 3. Complete logic, passing back whether image exists
      setTimeout(() => {
        if (typeof onDone === "function") {
          onDone(hasImage);
        }
      }, 600);

    } catch (err) {
      console.error("Failed to save or check image status:", err);
      setStatus("error");
    }
  };

  return (
    <div className="address-editor">
      <h2 className="ae-title">Confirm / Edit Address</h2>

      {loading && (
        <div className="ae-loading">
          Waiting for address from Google Maps...
        </div>
      )}

      {!loading && (
        <>
          <label className="ae-label">Address detected from Google:</label>
          <div className="ae-original">{originalAddress}</div>

          <label className="ae-label">Edit address :</label>
          <textarea
            className="ae-textarea"
            value={editedAddress}
            onChange={(e) => setEditedAddress(e.target.value)}
            rows={4}
            aria-label="Edit address"
          />

          <div className="ae-actions">
            <button
              className="ae-button"
              onClick={handleSave}
              disabled={status === "saving"}
            >
              {status === "saving" ? "Saving..." : "Save & Confirm Address"}
            </button>
            <button
              className="ae-button"
              onClick={() => onDone?.(null)}
              disabled={status === "saving"}
              style={{
                marginTop: "10px",
                marginBottom: "20px",
                backgroundColor: "#757575",
                borderColor: "#757575"
              }}
            >
              Back to Dashboard
            </button>
          </div>

          {status === "saved" && (
            <div className="ae-status ae-status-success">
              Saved. Moving...
            </div>
          )}
          {status === "error" && (
            <div className="ae-status ae-status-error">
              Failed to save. Try again.
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* =======================
   PropTypes validation
   ======================= */

AddressEditor.propTypes = {
  userID: PropTypes.string.isRequired,
  uniqueKey: PropTypes.string.isRequired,
  onDone: PropTypes.func,
};